<template>
  <!--
    The Vue 2 version placed this <div> outside any <template> block, so
    vue-loader treated it as an unknown custom block and the component rendered
    nothing at all while redirecting.
  -->
  <div class="page redirect-screen">
    <TnNotificationInline v-if="error" horizontal status="critical" full-width>
      {{ t(error) }}
    </TnNotificationInline>
    <TnParagraph v-else>{{ t('generic.loading') }}</TnParagraph>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { TnNotificationInline, TnParagraph } from '@telenornorgeinternal/telegrafen-vue3-core';

import { EnvironmentService } from '@/environment';
import {
  createDeviceUserManager,
  DeviceFlowService,
  storeDeviceClient,
} from './device-flow.service';
import type { DeviceCodeRequest } from '@/types';

const { t } = useI18n();
const route = useRoute();

/** Holds an i18n path (not a ready-made message), resolved with `t()` above. */
const error = ref<string | null>(null);

onMounted(async () => {
  try {
    const userCode = (route.query.userCode as string) ?? null;

    const [request, environment] = await Promise.all([
      new DeviceFlowService().getDeviceRequest(userCode),
      EnvironmentService.getEnvironment(),
    ]);

    // getDeviceRequest resolves to an ApiError instead of throwing.
    if (!('clientId' in request)) {
      error.value = request.i18nPath;
      return;
    }

    const { clientId, scopes } = request as DeviceCodeRequest;
    const client = { authority: environment.value, clientId, scopes };

    // Persist so the consent callback can rebuild the same UserManager.
    storeDeviceClient(client);

    await createDeviceUserManager(client).signinRedirect({
      state: userCode, // retrieved again at the callback
      extraQueryParams: {
        ui_locales: 'no en',
        context: 'login',
        user_code: userCode ?? '',
      },
    });
  } catch (err) {
    // Never surface raw exception text to the user - log it, show the generic message.
    error.value = 'generic.errors.requestError';
    console.error(err);
  }
});
</script>

<style scoped>
.redirect-screen {
  padding-block: 32px;
  text-align: center;
}
</style>
